/**
 * What `ThreadsPane` and the Workflows page ask the agent about workflows, and
 * the hook that runs the registered sections; `lib/workflowSections.ts` decides
 * what the pane shows from their slots.
 */

import { useEffect, useState } from "react";
import type { WorkflowSectionId } from "../../../shared/workflow-listing";
import { agentClient, type AgentTarget } from "../lib/agentClient";
import { fetchWorkflowList, type WorkflowListResult } from "../lib/workflowList";
import type { SectionSlot } from "../lib/workflowSections";
import type { WorkflowSection, WorkflowSectionContext } from "./types";

export type WorkflowListState = { kind: "loading" } | WorkflowListResult;

/**
 * The agent's workflow list (`GET /workflows`). Asked again whenever the agent
 * moves, and whenever the window comes back to the front: `ready` follows the CAD
 * instances connected right now, and the user opens Revit *beside* the app.
 * Without an agent there is nothing to ask — `unavailable`, and the page says why.
 */
export function useWorkflowList(agent: AgentTarget): WorkflowListState {
  const [state, setState] = useState<WorkflowListState>({ kind: "loading" });
  const baseUrl = agent.ok ? agent.baseUrl : null;
  useEffect(() => {
    if (!baseUrl) {
      setState({ kind: "unavailable" });
      return;
    }
    let cancelled = false;
    // A list already read stays while the agent's new address is asked again (a
    // restart moves its port): going back to "loading" hides a workflow's
    // conversation behind "Loading..." mid-run, for an answer that has not changed.
    setState((previous) => (previous.kind === "ok" ? previous : { kind: "loading" }));
    const refresh = () => {
      void fetchWorkflowList(agentClient).then((result) => {
        if (!cancelled) setState(result);
      });
    };
    refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [baseUrl]);
  return state;
}

/**
 * Each section's view. Every hook runs on every render, in registry order (the
 * list is a build-time constant, so the order cannot change); a section the
 * workflow on screen does not declare is told `enabled: false`, and
 * `composeSections` drops whatever it returns anyway. With no workflow on
 * screen, `declared` is empty and the pane is the core chat.
 */
export function useWorkflowSections(
  registered: readonly WorkflowSection[],
  base: Omit<WorkflowSectionContext, "enabled" | "panelOpen" | "setPanelOpen">,
  declared: readonly WorkflowSectionId[],
  panels: { open: string | null; onOpen: (id: string) => void; onClose: (id: string) => void },
): SectionSlot[] {
  return registered.map((section) => {
    const enabled = declared.includes(section.id);
    const view = section.useView({
      ...base,
      enabled,
      panelOpen: enabled && panels.open === section.id,
      setPanelOpen: (open) => (open ? panels.onOpen(section.id) : panels.onClose(section.id)),
    });
    return { id: section.id, enabled, view };
  });
}
